import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { t } from 'i18next';
import { Helmet } from 'react-helmet';
import axios from 'axios';

import { fetchUserByToken } from '../../../app/features/userSlice';
import Button from '../../../components/UI/Buttons/Button/Button';
import styles from './PaymentConfirmation.module.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Helper function to get plan name from price ID
const getPlanNameFromPriceId = (priceId) => {
    let planName = '';
    let duration = '';

    // Determine plan type
    if (priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH || 
        priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR) {
        planName = t('employer_plan.Basic');
    } else if (priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_MONTH || 
               priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_YEAR) {
        planName = t('employer_plan.SME');
    } else {
        planName = t('employer_plan.Basic');
    }

    // Determine duration
    if (priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH || 
        priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_MONTH) {
        duration = t('general.UI.monthly');
    } else if (priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR || 
               priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_YEAR) {
        duration = t('general.UI.annually');
    } else {
        duration = t('general.UI.monthly');
    }

    return `${planName} ${duration}`;
};

function PaymentConfirmation() {
    const location = useLocation();
    const { clientSecret, subscriptionId, priceId } = location.state || {};
    const [isRedirecting, setIsRedirecting] = useState(false);
    
    if (!clientSecret) {
        return (
            <div className={styles.container}>
                <h2>Payment information missing</h2>
                <p>Please go back to the pricing page and try again.</p>
                <Button onClick={() => window.history.back()}>Go Back</Button>
            </div>
        );
    }

    if (isRedirecting) {
        return (
            <div className={styles.container}>
                <h2>Payment Successful!</h2>
                <div className={styles.loaderContainer}>
                    <div className={styles.loader}></div>
                    <p>Redirecting to billing page...</p>
                </div>
            </div>
        );
    }

    const options = {
        clientSecret,
        appearance: {
            theme: 'stripe',
        },
    };

    return (
        <div className={styles.container}>
            <Helmet>
                <title>FindCV - Payment Confirmation</title>
            </Helmet>
            <h2>Complete Your Payment</h2>
            <Elements stripe={stripePromise} options={options}>
                <CheckoutForm 
                    subscriptionId={subscriptionId} 
                    setIsRedirecting={setIsRedirecting}
                />
            </Elements>
        </div>
    );
}

// Helper function to update unpaid invoices to paid status
const updateInvoiceStatusAfterPayment = async (userEmail) => {
    try {
        // Make a request to get recent invoices
        const response = await axios.get(`${process.env.REACT_APP_API_URL}payment/getInvoices`, {
            params: { email: userEmail, limit: 5 } // Get the most recent 5 invoices
        });
        
        // Find any unpaid invoices
        const unpaidInvoices = response.data.userInvoices.filter(inv => inv.status === 'unpaid');
        
        if (unpaidInvoices.length > 0) {
            console.log('Found unpaid invoices to update:', unpaidInvoices.length);
            // The webhook should handle this automatically, but we can trigger a refresh
            // by fetching the user data again
            return true;
        }
        
        return false;
    } catch (error) {
        console.error('Error checking invoice status:', error);
        return false;
    }
};

function CheckoutForm({ subscriptionId, setIsRedirecting }) {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userToken = useSelector(state => state.user.token);
    const userEmail = useSelector(state => state.user.user?.email);
    const user = useSelector(state => state.user.user);
    const location = useLocation();
    const { clientSecret, priceId } = location.state || {};

    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [countdown, setCountdown] = useState(null);

    useEffect(() => {
        if (!stripe) {
            return;
        }

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent.status) {
                case "succeeded":
                    setMessage("Payment succeeded!");
                    startRedirectCountdown();
                    break;
                case "processing":
                    setMessage("Your payment is processing.");
                    break;
                case "requires_payment_method":
                    setMessage("Please provide your payment details to continue.");
                    break;
                default:
                    setMessage("Something went wrong.");
                    break;
            }
        });
    }, [stripe, clientSecret]);

    // Countdown effect
    useEffect(() => {
        if (countdown !== null && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (countdown === 0) {
            setIsRedirecting(true);
            // Check for unpaid invoices before navigating
            if (userEmail) {
                updateInvoiceStatusAfterPayment(userEmail)
                    .then(() => {
                        // Refresh user data in any case
                        dispatch(fetchUserByToken(userToken));
                        setTimeout(() => {
                            navigate('/profile/billing', { replace: true });
                        }, 500);
                    });
            } else {
                dispatch(fetchUserByToken(userToken));
                setTimeout(() => {
                    navigate('/profile/billing', { replace: true });
                }, 500);
            }
        }
    }, [countdown, navigate, userToken, dispatch, setIsRedirecting, userEmail]);

    const startRedirectCountdown = () => {
        setCountdown(5);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsLoading(true);
        setMessage(null);
        console.log("handleSubmit");
        try {
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setMessage(submitError.message);
                setIsLoading(false);
                return;
            }

            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: `${window.location.origin}/profile/billing`,
                },
                redirect: 'if_required',
            });

            if (error) {
                if (error.type === "card_error" || error.type === "validation_error") {
                    setMessage(error.message);
                } else {
                    setMessage("An unexpected error occurred during payment confirmation.");
                    console.error("Payment error:", error);
                }
                setIsLoading(false);
            } else {
                // Payment successful
                setMessage("Payment successful!");
                
                // Check for any unpaid invoices that need to be updated
                if (userEmail) {
                    await updateInvoiceStatusAfterPayment(userEmail);
                    
                    // Send subscription activation notification with correct plan name
                    try {
                        const planName = getPlanNameFromPriceId(priceId);
                        await axios.post(`${process.env.REACT_APP_API_URL}notifications/send-subscription-activated`, {
                            employer: user,
                            subscription: paymentIntent,
                            planName: planName
                        });
                    } catch (error) {
                        console.error('Error sending subscription activation notification:', error);
                        // Don't block the payment success flow
                    }
                }
                
                startRedirectCountdown();
            }
        } catch (error) {
            console.error("Payment submission error:", error);
            setMessage("An error occurred while processing your payment. Please try again.");
            setIsLoading(false);
        }
    };

    return (
        <form id="payment-form" onSubmit={handleSubmit} className={styles.paymentForm}>
            <PaymentElement id="payment-element" options={{
                layout: 'tabs'
            }} />
            <Button disabled={isLoading || !stripe || !elements} type="submit">
                {isLoading ? "Processing..." : "Pay Now"}
            </Button>
            {message && <div className={`${styles.paymentMessage} ${message.includes('error') || message.includes('wrong') ? styles.error : ''}`}>{message}</div>}
        </form>
    );
}

export default PaymentConfirmation; 